import {
  ArcgisEndpointConstant,
  IOGCRepository,
  OGCFileEntity,
  OGCServerEntity,
  OGCServerTypeEnum,
  OGCFileFormatEnum,
  OGCCapabilityLayerEntity,
  OGCCapabilitiesValueObject,
  ResponseValueObject,
  GeoJsonValueObject,
  FeatureValueObject,
  IConfiguration,
} from '@gishub/domain';
import { axiosClient } from '../http/axios-client';

export class OGCRepository implements IOGCRepository {
  // Heurística de versiones: primero la versión actual, luego la previa, y así
  // sucesivamente, para no perder funcionalidad con servicios antiguos.
  private static readonly WMS_VERSIONS = ['1.3.0', '1.1.1', '1.0.0'];
  private static readonly WFS_VERSIONS = ['2.0.0', '1.1.0', '1.0.0'];

  constructor(private readonly configuration: IConfiguration) {}

  async getSupportedFormats(): Promise<ResponseValueObject<OGCFileEntity[]>> {
    return new ResponseValueObject(true, [
      { format: OGCFileFormatEnum.Shapefile, label: 'Shapefile (.zip)', accept: '.zip' },
      { format: OGCFileFormatEnum.GeoJSON, label: 'GeoJSON', accept: '.geojson,.json' },
      { format: OGCFileFormatEnum.KML, label: 'KML', accept: '.kml' },
      { format: OGCFileFormatEnum.KMZ, label: 'KMZ', accept: '.kmz' },
      { format: OGCFileFormatEnum.CSV, label: 'CSV (lat, lon)', accept: '.csv' },
      { format: OGCFileFormatEnum.TXT, label: 'TXT (lat, lon)', accept: '.txt' },
      { format: OGCFileFormatEnum.XLSX, label: 'Excel (.xlsx)', accept: '.xlsx' },
    ]);
  }

  async getExampleServers(): Promise<ResponseValueObject<OGCServerEntity[]>> {
    return new ResponseValueObject(true, [
      {
        type: OGCServerTypeEnum.WMS,
        label: 'SISFOR - Permisos TH (WMS)',
        url: 'https://sisfor.osinfor.gob.pe/osinfor/services/SISFOR_v5/PUBLICABLE_TH_PERMISOS/MapServer/WMSServer',
      },
      {
        type: OGCServerTypeEnum.WFS,
        label: 'ANA - DUA Acuícola WFS',
        url: ArcgisEndpointConstant.ANA_DUA_ACUICOLA_WFS,
      },
    ]);
  }

  /**
   * Recupera las capacidades de un servicio WMS/WFS con dos heurísticas:
   *
   * 1. VERSIÓN: se prueba la versión actual (WMS 1.3.0 / WFS 2.0.0) y, si falla
   *    (red, CORS o respuesta sin capas), se prueba la anterior, y la anterior,
   *    y así sucesivamente para no perder funcionalidad con servicios antiguos.
   *
   * 2. PROXY: si la URL no pertenece a la propia API y hay proxy configurado,
   *    se intenta primero la conexión directa y, si todas las versiones fallan
   *    por red/CORS, se reintenta el mismo barrido de versiones vía proxy.
   *
   * Se devuelve el `effectiveUrl` (directo o vía proxy) para que el consumidor
   * (WMSLayer/WFSLayer) use el mismo canal que funcionó.
   */
  async getCapabilities(url: string, serviceType: OGCServerTypeEnum): Promise<ResponseValueObject<OGCCapabilitiesValueObject>> {
    const versions =
      serviceType === OGCServerTypeEnum.WMS
        ? OGCRepository.WMS_VERSIONS
        : OGCRepository.WFS_VERSIONS;

    // Canales candidatos: primero directo, luego vía proxy (si aplica).
    const channels: Array<{ label: string; effectiveUrl: string; capUrl: (version: string) => string }> = [
      {
        label: 'directo',
        effectiveUrl: url,
        capUrl: v => this.buildCapabilitiesUrl(url, serviceType, v),
      },
    ];
    if (this.configuration.proxyUrl && !this.belongsToApi(url)) {
      channels.push({
        label: 'vía proxy',
        effectiveUrl: `${this.configuration.proxyUrl}${url}`,
        capUrl: v => `${this.configuration.proxyUrl}${this.buildCapabilitiesUrl(url, serviceType, v)}`,
      });
    }

    const tried: string[] = [];
    for (const channel of channels) {
      for (const version of versions) {
        const attempt = `${channel.label} v${version}`;
        try {
          const xml = await this.fetchText(channel.capUrl(version));
          const layers = this.parseCapabilities(xml, serviceType);
          if (layers.length > 0) {
            return new ResponseValueObject(true, new OGCCapabilitiesValueObject(layers, channel.effectiveUrl));
          }
          // El servicio respondió pero sin capas para esa versión: se intenta la previa.
          tried.push(`${attempt}: respuesta sin capas`);
        } catch (err) {
          tried.push(`${attempt}: ${(err as Error)?.message ?? err}`);
        }
      }
    }

    return new ResponseValueObject<OGCCapabilitiesValueObject>(
      false,
      undefined,
      `No se pudo conectar al servicio ${serviceType} probando versiones ${versions.join(', ')} ` +
        `(${channels.map(c => c.label).join(' y ')}). ${tried[tried.length - 1] ?? 'Verifica la URL.'}`,
    );
  }

  private buildCapabilitiesUrl(url: string, serviceType: OGCServerTypeEnum, version: string): string {
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}SERVICE=${serviceType}&VERSION=${version}&REQUEST=GetCapabilities`;
  }

  private belongsToApi(url: string): boolean {
    const api = this.configuration.apiUrl.replace(/\/+$/, '');
    return url.startsWith(api);
  }

  private async fetchText(url: string): Promise<string> {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`HTTP ${response.status} ${response.statusText}`);
    return response.text();
  }

  // ─── Parsing unificado WMS/WFS (solo cambia el contenedor/tag por tipo de servicio) ───

  private parseCapabilities(xmlText: string, serviceType: OGCServerTypeEnum): OGCCapabilityLayerEntity[] {
    const doc = new DOMParser().parseFromString(xmlText, 'text/xml');
    if (serviceType === OGCServerTypeEnum.WMS) {
      return this.parseWmsTree(doc);
    }
    return this.parseWfsList(doc);
  }

  private parseWmsTree(doc: Document): OGCCapabilityLayerEntity[] {
    const result: OGCCapabilityLayerEntity[] = [];
    const capability = this.directChild(doc.documentElement, 'Capability');
    const rootLayers = capability ? this.directChildren(capability, 'Layer') : [];

    const walk = (node: Element, depth: number): void => {
      const name = this.directChildText(node, 'Name');
      const title = (this.directChildText(node, 'Title') || name || 'Capa').trim();
      const children = this.directChildren(node, 'Layer');

      if (name) {
        result.push({ name, title, isGroup: false, depth });
        children.forEach(c => walk(c, depth + 1));
      } else if (children.length > 0) {
        result.push({ name: '', title, isGroup: true, depth });
        children.forEach(c => walk(c, depth + 1));
      }
    };

    rootLayers.forEach(l => walk(l, 0));
    return result;
  }

  private parseWfsList(doc: Document): OGCCapabilityLayerEntity[] {
    const result: OGCCapabilityLayerEntity[] = [];
    // WFS 1.x usa <FeatureType>; WFS 2.0 usa <wfs:FeatureType>. getElementsByTagNameNS
    // con "*" hace match por localName, sin importar el prefijo del namespace.
    const featureTypes = doc.getElementsByTagNameNS('*', 'FeatureType');
    for (let i = 0; i < featureTypes.length; i++) {
      const name = this.directChildText(featureTypes[i], 'Name');
      if (name) {
        const title = this.directChildText(featureTypes[i], 'Title') || name;
        result.push({ name, title, isGroup: false, depth: 0 });
      }
    }
    return result;
  }

  private directChildren(parent: Element, tag: string): Element[] {
    const out: Element[] = [];
    for (let i = 0; i < parent.children.length; i++) {
      if (parent.children[i].localName === tag) out.push(parent.children[i]);
    }
    return out;
  }

  private directChild(parent: Element, tag: string): Element | null {
    return this.directChildren(parent, tag)[0] ?? null;
  }

  private directChildText(parent: Element, tag: string): string {
    const child = this.directChild(parent, tag);
    return child?.textContent?.trim() ?? '';
  }

  // ─── Subida de archivos ───────────────────────────────────────────────────────

  /**
   * Sube un archivo OGC (shapefile, GeoJSON, KML/KMZ, CSV/TXT, XLSX) al backend
   * (`POST /api/v1/spatial/upload`, multipart campo `file`, JWT).
   * Siempre viaja al endpoint: el backend normaliza y responde un GeoJSON
   * FeatureCollection en WGS84, independientemente del formato de origen.
   */
  async uploadFile(file: File): Promise<ResponseValueObject<GeoJsonValueObject>> {
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axiosClient.post<GeoJsonValueObject>('/spatial/upload', formData, {
        headers: { 'Content-Type': null },
        timeout: 60_000,
      });
      return new ResponseValueObject(true, OGCRepository.toGeoJsonValueObject(response.data));
    } catch (e: any) {
      return new ResponseValueObject<GeoJsonValueObject>(false, undefined, e?.message ?? 'Error al subir el archivo');
    }
  }

  private static toGeoJsonValueObject(payload: any): GeoJsonValueObject {
    // El backend puede responder la FeatureCollection directa o envuelta en { data: ... }
    const fc = payload?.data && payload.data.type === 'FeatureCollection' ? payload.data : payload;
    if (!fc || fc.type !== 'FeatureCollection' || !Array.isArray(fc.features)) {
      throw new Error('El servidor no devolvió un GeoJSON válido');
    }
    const features = fc.features.map((f: any) => FeatureValueObject.fromRaw(f));
    return new GeoJsonValueObject(fc.type, features, fc);
  }
}
